import astm from "@/images/icons/astm.svg";
import countAndConcentration from "@/images/icons/count-and-concentration.svg";
import filtration from "@/images/icons/filtration.svg";
import firstDerivative from "@/images/icons/first-derivative.svg";
import fracture from "@/images/icons/fracture.svg";
import frontTracking from "@/images/icons/front-tracking.svg";
import integration from "@/images/icons/integration.svg";
import lumview from "@/images/icons/lumview.svg";
import mwlRatio from "@/images/icons/mwl-ratio.svg";
import mwl from "@/images/icons/mwl.svg";
import sedimentationHeight from "@/images/icons/sedimentation-height.svg";
import spocview from "@/images/icons/spocview.svg";
import stabilityComparative from "@/images/icons/Stability_comparative.svg";
import stabilityIndex from "@/images/icons/Stability_index.svg";
import stabilityPredictive from "@/images/icons/Stability_predictive.svg";
import velocityPsa from "@/images/icons/velocity-psa.svg";
import volumePsa from "@/images/icons/volume-psa.svg";

import React from "react";
import {injectIntl, IntlShape} from "react-intl";

import {EAnalysisTasks, EModules} from "@/models";
import {ILocalizedText} from "@translate/models";
import {IModuleInfo} from "./models";

import {trademark} from "@/Footer";
import {intl2Str} from "@translate/T";
import Highlighter from "./Highlighter";

export function getModuleInfo(engine: EModules): IModuleInfo {
    switch (engine) {
        case EModules.Frac:
            return {
                icon: fracture,
                type: (intl) => intl2Str(intl, "LUMiFrac analysis"),
            };

        default:
        case EModules.LumView:
            return {
                icon: lumview,
                type: (intl) =>
                    intl2Str(intl, "LUMView{trademark} analysis", {
                        trademark: trademark(),
                    }),
            };

        case EModules.Instability:
            return {
                icon: stabilityIndex,
                type: (intl) => intl2Str(intl, "Stability analysis"),
            };

        case EModules.Integration:
            return {
                icon: integration,
                type: (intl) => intl2Str(intl, "Integration analysis"),
            };

        case EModules.FrontTracking:
            return {
                icon: frontTracking,
                type: (intl) => intl2Str(intl, "Front tracking analysis"),
            };
        case EModules.PsaVelocityPos:
        case EModules.PsaVelocityTime:
            return {
                icon: velocityPsa,
                type: (intl) =>
                    intl2Str(intl, "Velocity distribution analysis"),
            };

        case EModules.PsaIntensityPos:
        case EModules.PsaIntensityTime:
            return {
                icon: volumePsa,
                type: (intl) =>
                    intl2Str(intl, "Extinction distribution analysis"),
            };

        case EModules.PsaVolumeWeightedPos:
        case EModules.PsaVolumeWeightedTime:
            return {
                icon: volumePsa,
                type: (intl) => intl2Str(intl, "Volume distribution analysis"),
            };

        case EModules.PsaNumberWeightedPos:
        case EModules.PsaNumberWeightedTime:
            return {
                icon: volumePsa,
                type: (intl) => intl2Str(intl, "Number distribution analysis"),
            };

        case EModules.Filtration:
            return {
                icon: filtration,
                type: (intl) => intl2Str(intl, "Filtration analysis"),
            };

        case EModules.SedimentationHeight:
            return {
                icon: sedimentationHeight,
                type: (intl) => intl2Str(intl, "Sedimentation height analysis"),
            };

        case EModules.FirstDerivative:
            return {
                icon: firstDerivative,
                type: (intl) => intl2Str(intl, "First derivative analysis"),
            };

        case EModules.MwlRatio:
            return {
                icon: mwlRatio,
                type: (intl) => intl2Str(intl, "MWL extinction ratio analysis"),
            };

        case EModules.Astm:
            return {
                icon: astm,
                type: (intl) =>
                    intl2Str(intl, "SEPCalc ASTM D7827-12 analysis"),
            };

        case EModules.PsaMwlVolumeWeighted:
            return {
                icon: mwl,
                type: (intl) =>
                    intl2Str(intl, "MWL volume distribution analysis"),
            };

        case EModules.PsaMwlNumberWeighted:
            return {
                icon: mwl,
                type: (intl) =>
                    intl2Str(intl, "MWL number distribution analysis"),
            };

        case EModules.SpocView:
            return {
                icon: spocview,
                type: (intl) => intl2Str(intl, "Spoc View analysis"),
            };

        case EModules.SpocAlignment:
            return {
                icon: spocview,
                type: (intl) => intl2Str(intl, "Alignment View"),
            };

        case EModules.SpocCount:
            return {
                icon: countAndConcentration,
                type: (intl) =>
                    intl2Str(intl, "Count & Concentration analysis"),
            };

        case EModules.SpocSizeVolume:
            return {
                icon: volumePsa,
                type: (intl) => intl2Str(intl, "Volume distribution analysis"),
            };

        case EModules.SpocSizeNumber:
            return {
                icon: volumePsa,
                type: (intl) => intl2Str(intl, "Number distribution analysis"),
            };
    }
}

export function getTaskInfo(task: EAnalysisTasks): IModuleInfo {
    const psdString = "Paricle size distribution";
    const separationString = "Separation";

    switch (task) {
        default:
        case EAnalysisTasks.LumView:
            return {
                icon: lumview,
                type: (intl) =>
                    intl2Str(intl, "LUMView{trademark}", {
                        trademark: trademark(),
                    }),
                header: (intl) => intl2Str(intl, separationString),
            };

        case EAnalysisTasks.StabilityIndex:
            return {
                icon: stabilityIndex,
                type: (intl) => intl2Str(intl, "Instability Index"),
            };

        case EAnalysisTasks.FrontTrackingComparative:
            return {
                icon: stabilityComparative,
                type: (intl) => intl2Str(intl, "Stability - comparative"),
            };

        case EAnalysisTasks.FrontTrackingPredictive:
            return {
                icon: stabilityPredictive,
                type: (intl) => intl2Str(intl, "Stability - predictive"),
            };

        case EAnalysisTasks.PsaVelocity:
            return {
                icon: velocityPsa,
                type: (intl) => intl2Str(intl, "Velocity distribution"),
                header: (intl) => intl2Str(intl, "Paricle characterization"),
            };

        case EAnalysisTasks.ExtinctionWeighted:
            return {
                icon: volumePsa,
                type: (intl) => intl2Str(intl, "Extinction weighted"),
                header: (intl) => intl2Str(intl, psdString),
            };

        case EAnalysisTasks.VolumeWeighted:
            return {
                icon: volumePsa,
                type: (intl) => intl2Str(intl, "Volume weighted"),
                header: (intl) => intl2Str(intl, psdString),
            };

        case EAnalysisTasks.NumberWeighted:
            return {
                icon: volumePsa,
                type: (intl) => intl2Str(intl, "Number weighted"),
                header: (intl) => intl2Str(intl, psdString),
            };

        case EAnalysisTasks.MwlVolumeWeighted:
            return {
                icon: mwl,
                type: (intl) => intl2Str(intl, "Volume weighted"),
                header: (intl) =>
                    intl2Str(intl, psdString + " - Multiple wavelengths"),
            };

        case EAnalysisTasks.MwlNumberWeighted:
            return {
                icon: mwl,
                type: (intl) => intl2Str(intl, "Number weighted"),
                header: (intl) =>
                    intl2Str(intl, psdString + " - Multiple wavelengths"),
            };

        case EAnalysisTasks.Integration:
            return {
                icon: integration,
                type: (intl) => intl2Str(intl, "Integration"),
                header: (intl) => intl2Str(intl, separationString),
            };

        case EAnalysisTasks.FrontTracking:
            return {
                icon: frontTracking,
                type: (intl) => intl2Str(intl, "Front tracking"),
                header: (intl) => intl2Str(intl, separationString),
            };

        case EAnalysisTasks.FirstDerivative:
            return {
                icon: firstDerivative,
                type: (intl) => intl2Str(intl, "First Derivative"),
                header: (intl) => intl2Str(intl, separationString),
            };

        case EAnalysisTasks.MWLExtinctionRatio:
            return {
                icon: mwlRatio,
                type: (intl) => intl2Str(intl, "MWL extinction ratio"),
                header: (intl) => intl2Str(intl, separationString),
            };

        case EAnalysisTasks.Filtration:
            return {
                icon: filtration,
                type: (intl) => intl2Str(intl, "Filtration"),
                header: (intl) => intl2Str(intl, separationString),
            };

        case EAnalysisTasks.SedimentationHeight:
            return {
                icon: sedimentationHeight,
                type: (intl) => intl2Str(intl, "Sedimentation height"),
                header: (intl) => intl2Str(intl, separationString),
            };
    }
}

interface IModuleIconProps {
    intl: IntlShape;
    icon: string;
    name?: string | ILocalizedText;
    highlighter?: string;
    task?: string | ILocalizedText;
}

class ModuleIcon extends React.PureComponent<IModuleIconProps> {
    public render() {
        const {intl, icon} = this.props;

        return (
            <React.Fragment>
                <img
                    className={"module-icon"}
                    alt={intl2Str(intl, "Module icon")}
                    src={icon}
                />
                {this.renderName()}
            </React.Fragment>
        );
    }

    private renderName() {
        const {intl, name, highlighter, task} = this.props;

        if (!name) {
            return null;
        }

        let nameString = typeof name === "string" ? name : name(intl);
        if (task)
            nameString +=
                typeof task === "string" ? " - " + task : " - " + task(intl);

        return (
            <span className="align-middle ml-1">
                <Highlighter query={highlighter} text={nameString} />
            </span>
        );
    }
}

export default injectIntl(ModuleIcon);
